import React from 'react'
import type { Block, Variable } from '../../types'
import { PropertyLabel, AlignPicker } from './shared'
import VariableTextarea from '../VariableTextarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select'

interface TextPropertiesProps {
  block: Block
  variables: Variable[]
  onUpdateBlock: (id: string, updates: Partial<Block>) => void
}

const STYLE_OPTIONS = [
  { value: 'paragraph', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'bold', label: 'Bold' },
  { value: 'list-bullet', label: 'Bullet List' },
  { value: 'list-ordered', label: 'Numbered List' },
  { value: 'quote', label: 'Quote' },
  { value: 'highlight', label: 'Highlight' },
  { value: 'callout-info', label: 'Callout — Info' },
  { value: 'callout-warning', label: 'Callout — Warning' },
  { value: 'callout-success', label: 'Callout — Success' },
  { value: 'callout-error', label: 'Callout — Error' },
] as const

export default function TextProperties({ block, variables, onUpdateBlock }: TextPropertiesProps) {
  const config = (block.config || {}) as Record<string, unknown>
  const currentStyle = (config.style as string) || 'paragraph'

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <PropertyLabel>Content</PropertyLabel>
        <VariableTextarea
          value={block.content || ''}
          onChange={(val) => onUpdateBlock(block.id, { content: val })}
          variables={variables}
          placeholder="Type @ to reference variables"
        />
      </div>
      <p className="text-[11px] text-slate-500 leading-relaxed">
        Use <code className="font-mono bg-slate-50 px-1 rounded">@variable_name</code> to insert dynamic values.
      </p>

      <div className="space-y-2">
        <PropertyLabel>Style</PropertyLabel>
        <Select
          value={currentStyle}
          onValueChange={(v) =>
            onUpdateBlock(block.id, { config: { ...config, style: v } })
          }
        >
          <SelectTrigger className="w-full">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {STYLE_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <PropertyLabel>Alignment</PropertyLabel>
        <AlignPicker
          value={((block.config as Record<string, unknown>)?.align as string) || 'left'}
          onChange={(v) => onUpdateBlock(block.id, { config: { ...config, align: v } })}
        />
      </div>
    </div>
  )
}
